import type RootStore from 'stores/RootStore'
import { trackInAmplitude } from 'utils/tracking/amplitude'
import { AmplitudeEvent } from 'utils/tracking/amplitude/amplitude.interface'
import hasSecondDebtor from 'utils/formDependencies/conditions/hasSecondDebtor'
import { DELIVERY_METHOD } from 'constants/documentDelivery'
import type { DeliveryMethod } from 'constants/documentDelivery'
import type { Data } from 'interfaces/ApolloResponse.interface'
import {
  followsFirstDebtor,
  getDocumentDeliveryMethod,
  isCompleteDocumentPreferences
} from 'utils/documentDeliveryPayload'
import { DocumentKey } from 'interfaces/documentDelivery.interface'

// Tracked values are spelled out so they stay readable in Amplitude.
const TRACKED_DELIVERY_METHOD: Record<DeliveryMethod, string> = {
  [DELIVERY_METHOD.EMAIL]: DELIVERY_METHOD.EMAIL,
  [DELIVERY_METHOD.POST]: DELIVERY_METHOD.POST,
  [DELIVERY_METHOD.PER_DOCUMENT]: 'per_document'
}

export const getDocumentDeliverySelection = (data: Data) => {
  const method = getDocumentDeliveryMethod(data)

  if (!method) {
    return {}
  }

  const deliveryMethod = TRACKED_DELIVERY_METHOD[method]
  const { primary, secondary } = data.debtors
  const hasSecond = hasSecondDebtor(data)
  const isSecondSameAsFirst = followsFirstDebtor(data)
  const primaryPreferences = primary.documentDeliveryPreferences
  // A second debtor that follows the first has the same set.
  const secondaryPreferences = isSecondSameAsFirst
    ? primaryPreferences
    : secondary.documentDeliveryPreferences

  if (
    !isCompleteDocumentPreferences(primaryPreferences) ||
    (hasSecond && !isCompleteDocumentPreferences(secondaryPreferences))
  ) {
    return { deliveryMethod }
  }

  return {
    deliveryMethod,
    primary: primaryPreferences,
    ...(hasSecond && {
      isSecondSameAsFirst,
      secondary: secondaryPreferences
    })
  }
}

/** Fired right before finalization; stays silent when the user never chose an option. */
export const trackDocumentDeliverySelection = ({
  traversal: { data }
}: RootStore) => {
  if (!getDocumentDeliveryMethod(data)) {
    return
  }

  trackInAmplitude(
    AmplitudeEvent.DocumentDeliverySelection,
    getDocumentDeliverySelection(data)
  )
}

/** The "ändern" link was clicked. */
export const trackDocumentDeliveryChangeLinkClicked = () =>
  trackInAmplitude(AmplitudeEvent.DocumentDeliveryChangeLinkClicked)

/** The checkbox shows its error because no option is selected. */
export const trackDocumentDeliveryOptionMissing = (
  debtorCount: number,
  trigger: 'submit' | 'deselect'
) =>
  trackInAmplitude(AmplitudeEvent.DocumentDeliveryOptionMissing, {
    debtorCount,
    trigger
  })

/** Errors are shown for document choices left open after picking "Einzeln anpassen". */
export const trackDocumentDeliveryChoiceMissing = (
  missing: Record<'primary' | 'secondary', DocumentKey[]>,
  secondDebtor?: { isSameAsFirst: boolean }
) =>
  trackInAmplitude(AmplitudeEvent.DocumentDeliveryChoiceMissing, {
    missingPrimary: missing.primary,
    ...(secondDebtor && {
      isSecondSameAsFirst: secondDebtor.isSameAsFirst,
      // A taken-over selection has no choices of its own that could be missing.
      ...(!secondDebtor.isSameAsFirst && {
        missingSecondary: missing.secondary
      })
    })
  })
